import { Reducer } from 'redux'
import { IAction, ICustomer, ICustomersState, ICustomerContact } from 'apps/fleet-planner/types'
import {
  FETCH_CUSTOMERS_ACTION_TYPES,
  CREATE_CUSTOMER_ACTION_TYPES,
  UPDATE_CUSTOMER_ACTION_TYPES,
  CREATE_CUSTOMER_CONTACT_ACTION_TYPES,
  SET_CUSTOMER,
} from 'apps/fleet-planner/utils/actions/customers'

const {
  FETCH_CUSTOMERS_FAILURE,
  FETCH_CUSTOMERS_REQUEST,
  FETCH_CUSTOMERS_SUCCESS,
} = FETCH_CUSTOMERS_ACTION_TYPES

const {
  CREATE_CUSTOMER_FAILURE,
  CREATE_CUSTOMER_REQUEST,
  CREATE_CUSTOMER_SUCCESS,
} = CREATE_CUSTOMER_ACTION_TYPES

const {
  UPDATE_CUSTOMER_FAILURE,
  UPDATE_CUSTOMER_REQUEST,
  UPDATE_CUSTOMER_SUCCESS,
} = UPDATE_CUSTOMER_ACTION_TYPES

const {
  CREATE_CUSTOMER_CONTACT_FAILURE,
  CREATE_CUSTOMER_CONTACT_REQUEST,
  CREATE_CUSTOMER_CONTACT_SUCCESS,
} = CREATE_CUSTOMER_CONTACT_ACTION_TYPES

const defaultState: ICustomersState = {
  fetching: false,
  customers: [],
  customersAutocomplete: [],
}

interface ISuccessAction extends IAction {
  payload: {
    customers: ICustomer[]
  }
}

interface ICustomerAction extends IAction {
  payload: {
    customer: ICustomer
  }
}

interface ICustomerContactAction extends IAction {
  payload: {
    contact: ICustomerContact
  }
}

type CustomerAction = ISuccessAction | ICustomerAction | ICustomerContactAction

const customersReducer: Reducer<ICustomersState, CustomerAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case FETCH_CUSTOMERS_REQUEST: {
      return {
        ...state,
        fetching: true,
        error: undefined,
      }
    }

    case FETCH_CUSTOMERS_SUCCESS: {
      const {
        payload: { customers },
      } = action as ISuccessAction
      return {
        ...state,
        customers,
        fetching: false,
      }
    }

    case FETCH_CUSTOMERS_FAILURE: {
      return {
        ...state,
        fetching: false,
        error: action.error,
      }
    }

    case CREATE_CUSTOMER_REQUEST:
    case CREATE_CUSTOMER_CONTACT_REQUEST: {
      return {
        ...state,
        saving: true,
        savingError: undefined,
      }
    }

    case CREATE_CUSTOMER_FAILURE:
    case CREATE_CUSTOMER_CONTACT_FAILURE:
    case UPDATE_CUSTOMER_FAILURE: {
      return {
        ...state,
        saving: false,
        savingError: action.error,
      }
    }

    case CREATE_CUSTOMER_SUCCESS:
    case UPDATE_CUSTOMER_REQUEST:
    case UPDATE_CUSTOMER_SUCCESS: {
      // Update store with new customer
      const currentCustomer = (action as ICustomerAction).payload.customer
      const customers = [
        ...state.customers.filter(c => c?.id !== currentCustomer?.id),
        currentCustomer,
      ]

      return {
        ...state,
        saving: false,
        currentCustomer,
        customers,
      }
    }

    case CREATE_CUSTOMER_CONTACT_SUCCESS: {
      const response = (action as ICustomerContactAction).payload.contact
      const customerId = response?.contactableId

      // Find customer
      const customer = state.customers?.find(c => c?.id === customerId)!

      // TODO: double check if that the best fallback
      if (!customer)
        return {
          ...state,
        }

      // Add contact to that customer's contacts
      customer.contacts.push(response)

      // Add update to state
      const updatedCustomers = [...state.customers.filter(c => c?.id !== customer?.id), customer]

      return {
        ...state,
        saving: false,
        customers: [...updatedCustomers],
        currentCustomer: state.currentCustomer
          ? {
              ...state.currentCustomer,
              contacts: [
                ...(state.currentCustomer?.contacts ?? []),
                (action as ICustomerContactAction).payload.contact,
              ],
            }
          : undefined,
      }
    }

    // Todo: Move this into CustomerInformation.tsx
    case SET_CUSTOMER: {
      return {
        ...state,
        currentCustomer: (action as ICustomerAction).payload.customer,
        savingError: undefined,
      }
    }

    default:
      return state
  }
}

export default customersReducer
